
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import AppIcon from "@/components/common/AppIcon.vue";
import CardKpi from "@/components/card/CardKpi.vue";
import CardGroup from "@/components/card/CardGroup.vue";

interface BounceRecommendation {
    type: "success" | "warning" | "error";
    message: string;
}

interface BounceData {
    reputation_score?: number;
    reputation_status?: "excellent" | "good" | "fair" | "poor";
    hard_bounce_rate?: number;
    soft_bounce_rate?: number;
    complaint_rate?: number;
    problem_contacts_count?: number;
    recommendations?: BounceRecommendation[];
}

export default defineComponent({
    name: "BounceMetrics",
    components: {
        AppIcon,
        CardKpi,
        CardGroup
    },
    props: {
        bounceData: {
            type: Object as PropType<BounceData>,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    setup(props) {
        const { t } = useI18n();
        
        const reputationIconColor = computed(() => {
            if (!props.bounceData?.reputation_status) return "text-gray-400";

            switch (props.bounceData.reputation_status) {
                case "excellent":
                    return "text-green-500";
                case "good":
                    return "text-blue-500";
                case "fair":
                    return "text-yellow-500";
                case "poor":
                    return "text-red-500";
                default:
                    return "text-gray-400";
            }
        });

        const getTranslatedRecommendation = (message: string): string => {
            const rateMatch = message.match(/\(>?([0-9.]+)%\)/);
            const rate = rateMatch ? rateMatch[1] : '';

            if (message.includes('Hard bounce rate is critically high')) {
                return t('pages.mails.recommendations.hardBounceCritical', { rate });
            } else if (message.includes('Hard bounce rate is elevated')) {
                return t('pages.mails.recommendations.hardBounceWarning', { rate });
            } else if (message.includes('Spam complaint rate is critically high')) {
                return t('pages.mails.recommendations.complaintCritical', { rate });
            } else if (message.includes('Spam complaint rate is elevated')) {
                return t('pages.mails.recommendations.complaintWarning', { rate });
            } else if (message.includes('Your bounce rates are within acceptable limits')) {
                return t('pages.mails.recommendations.bounceRatesGood');
            }
            
            return message;
        };

        return {
            reputationIconColor,
            getTranslatedRecommendation
        };
    }
});
